import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Spinner from '../../components/Spinner';

const PER_PAGINA = 15;

function badgePratica(richiesta, numero) {
  const tipo = (richiesta || '').toLowerCase();
  const n = numero || '';
  if (tipo.includes('iscriz')) return `Isc.${n}`;
  if (tipo.includes('revis') || tipo.includes('verif') || tipo.includes('triennal')) return `Rev.${n}`;
  if (tipo.includes('rinnov')) return `Rin.${n}`;
  if (tipo.includes('variat')) return `Var.${n}`;
  return `${richiesta || '?'} ${n}`.trim();
}

function coloreBadge(richiesta) {
  const tipo = (richiesta || '').toLowerCase();
  if (tipo.includes('iscriz')) return 'bg-blue-100 text-blue-700';
  if (tipo.includes('revis') || tipo.includes('verif') || tipo.includes('triennal')) return 'bg-green-100 text-green-700';
  if (tipo.includes('rinnov')) return 'bg-purple-100 text-purple-700';
  return 'bg-gray-100 text-gray-600';
}

export default function ListaImprese() {
  const [imprese, setImprese] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [pagina, setPagina] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get('/api/imprese')
      .then(r => setImprese(r.data))
      .finally(() => setLoading(false));
  }, []);

  // Raggruppa per ragione_sociale (case-insensitive)
  const gruppi = (() => {
    const map = new Map();
    for (const imp of imprese) {
      const key = imp.ragione_sociale.trim().toLowerCase();
      if (!map.has(key)) {
        map.set(key, { ...imp, pratiche: [imp] });
      } else {
        map.get(key).pratiche.push(imp);
      }
    }
    return [...map.values()];
  })();

  const filtrati = gruppi.filter(g =>
    g.ragione_sociale.toLowerCase().includes(filtro.toLowerCase()) ||
    (g.citta || '').toLowerCase().includes(filtro.toLowerCase()) ||
    (g.partita_iva || '').includes(filtro)
  );

  const nPagine = Math.max(1, Math.ceil(filtrati.length / PER_PAGINA));
  const paginaCorretta = Math.min(pagina, nPagine);
  const paginata = filtrati.slice((paginaCorretta - 1) * PER_PAGINA, paginaCorretta * PER_PAGINA);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-[#1A3A5C]">Imprese</h1>
        <button
          onClick={() => navigate('/admin/imprese/nuova')}
          className="bg-[#1A3A5C] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#2E6096] transition"
        >
          + Nuova impresa
        </button>
      </div>

      <input
        type="text"
        placeholder="Cerca per ragione sociale, città, P.IVA..."
        value={filtro}
        onChange={e => { setFiltro(e.target.value); setPagina(1); }}
        className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
      />

      {!loading && (
        <p className="text-sm text-gray-500 mb-2">
          {filtrati.length} {filtrati.length === 1 ? 'impresa' : 'imprese'}
          {filtro && ` su ${gruppi.length} totali`}
        </p>
      )}

      {loading ? <Spinner /> : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          {filtrati.length === 0 ? (
            <p className="text-center text-gray-500 py-12">Nessuna impresa trovata</p>
          ) : (<>
            {/* Card view — mobile */}
            <div className="sm:hidden divide-y divide-gray-100">
              {paginata.map(g => (
                <div key={g.id} className="p-4 active:bg-[#EEF4FB]"
                  onClick={() => navigate(`/admin/gruppo-impresa/${encodeURIComponent(g.ragione_sociale)}`)}>
                  <p className="font-semibold text-[#1A3A5C]">{g.ragione_sociale}</p>
                  <p className="text-sm text-gray-500 mt-0.5">{g.citta}{g.provincia ? ` (${g.provincia})` : ''}</p>
                  {g.partita_iva && <p className="text-xs text-gray-400 mt-0.5">P.IVA {g.partita_iva}</p>}
                  <p className="text-xs text-[#2E6096] mt-1 font-medium">Apri →</p>
                </div>
              ))}
            </div>
            {/* Tabella — desktop */}
            <table className="hidden sm:table w-full text-sm">
              <thead className="bg-[#EEF4FB] text-[#1A3A5C] font-semibold">
                <tr>
                  <th className="text-center px-4 py-3 w-10 text-gray-400">#</th>
                  <th className="text-left px-4 py-3">Ragione sociale</th>
                  <th className="text-left px-4 py-3">Città</th>
                  <th className="text-left px-4 py-3">P.IVA</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {paginata.map((g, i) => (
                  <tr key={g.id} className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-[#EEF4FB] cursor-pointer transition`}
                    onClick={() => navigate(`/admin/gruppo-impresa/${encodeURIComponent(g.ragione_sociale)}`)}>
                    <td className="px-4 py-3 text-center text-gray-400 text-xs">{(paginaCorretta - 1) * PER_PAGINA + i + 1}</td>
                    <td className="px-4 py-3 font-medium text-[#1A3A5C]">{g.ragione_sociale}</td>
                    <td className="px-4 py-3 text-gray-600">{g.citta} {g.provincia ? `(${g.provincia})` : ''}</td>
                    <td className="px-4 py-3 text-gray-600">{g.partita_iva || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-[#2E6096] font-medium">Apri →</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>)}
        </div>
      )}

      {!loading && nPagine > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button onClick={() => setPagina(1)} disabled={paginaCorretta === 1}
            className="px-3 py-1 rounded text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-100">«</button>
          <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={paginaCorretta === 1}
            className="px-3 py-1 rounded text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-100">‹</button>
          {Array.from({ length: nPagine }, (_, i) => i + 1)
            .filter(p => p === 1 || p === nPagine || Math.abs(p - paginaCorretta) <= 2)
            .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
            .map((p, idx) => p === '...'
              ? <span key={`e${idx}`} className="px-2 text-gray-400">…</span>
              : <button key={p} onClick={e => { e.stopPropagation(); setPagina(p); }}
                  className={`px-3 py-1 rounded text-sm border ${p === paginaCorretta ? 'bg-[#1A3A5C] text-white border-[#1A3A5C]' : 'border-gray-300 hover:bg-gray-100'}`}
                >{p}</button>
            )}
          <button onClick={() => setPagina(p => Math.min(nPagine, p + 1))} disabled={paginaCorretta === nPagine}
            className="px-3 py-1 rounded text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-100">›</button>
          <button onClick={() => setPagina(nPagine)} disabled={paginaCorretta === nPagine}
            className="px-3 py-1 rounded text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-100">»</button>
        </div>
      )}
    </div>
  );
}
